import DetailSynopsis from './DetailSynopsis';
import { CollectionPicker } from './MovieCollections';
import React, { useEffect, useRef, useState } from "react";
import useArtworkTheme from './useArtworkTheme';
import { createPortal } from "react-dom";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import Card from "react-bootstrap/Card";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { Button, Tabs, Tab } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import Review from "./Seriesreview";
import CastList from "./Seriescast";
import Api_key from "./apiconfiguration";
import './seriesdetails.css';
import DetailGlassLoading from './DetailGlassLoading';
import { FiPlay, FiClock, FiCalendar, FiChevronDown, FiX } from "react-icons/fi";
import useLoadTransition from "./useLoadTransition";
import usePlayerControls from "./usePlayerControls";
import MovieChat from "./MovieChat";
import preloadImage from "./preloadImage";
import { LikeButton, WatchlistButton } from "./LibraryActions";
import { useAuth } from "./AuthContext";
import CinematicInsights from "./CinematicInsights";
import PlayerExtras from './PlayerExtras';
import useViewingSession from './useViewingSession';

const SeriesDetails = () => {
  const { id } = useParams();
  const [series, setSeries] = useState(null);
  useArtworkTheme(series?.backdrop_path || series?.poster_path);
  const [similarSeries, setSimilarSeries] = useState([]);
  const [showPlayer, setShowPlayer] = useState(false);
  const [isPlayerFullscreen, setIsPlayerFullscreen] = useState(false);
  const [episodes, setEpisodes] = useState([]);
  const [selectedSeason, setSelectedSeason] = useState(1);
  const [playerData, setPlayerData] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);
  const playerOverlayRef = useRef(null);
  const watchStartedAt = useRef(null);
  const { showLoader, isContentVisible } = useLoadTransition(!series);
  const { controlsVisible, showControls } = usePlayerControls(showPlayer);
  const apiKey = Api_key;
  const navigate = useNavigate();
  const { user, experience, saveExperienceItem } = useAuth();
  useViewingSession(showPlayer, series, 'tv', playerOverlayRef);

  const beginEpisodeProgress = (season, episode) => {
    if ("mediaSession" in navigator && window.MediaMetadata) {
      navigator.mediaSession.metadata = new window.MediaMetadata({
        title:`${series.name} · S${season} E${episode}`, artist:"Hedone",
        artwork:series.poster_path ? [{src:`https://image.tmdb.org/t/p/w500${series.poster_path}`,sizes:"500x750",type:"image/jpeg"}] : [],
      });
    }
    watchStartedAt.current = Date.now();
    if (!user) return;
    const key = `tv_${id}`;
    const previous = experience.progress?.[key] || {};
    saveExperienceItem("progress", key, {
      ...previous, id:Number(id), mediaType:"tv", title:series.name,
      posterPath:series.poster_path || null, backdropPath:series.backdrop_path || null,
      season, episode, durationSeconds:2700, progressSeconds:0, lastWatchedAt:Date.now(),
    }).catch(() => {});
  };

  const handleWatchNow = () => {
    if (episodes.length > 0) {
      beginEpisodeProgress(selectedSeason, episodes[0].episode_number);
      window.history.pushState({ ...window.history.state, hedonePlayer: true }, '');
      setPlayerData({
        id: series.id,
        season: selectedSeason,
        episode: episodes[0].episode_number,
      });
      setShowPlayer(true);
    }
  };

  const handlePlayEpisode = (season, episode) => {
    beginEpisodeProgress(season, episode);
    window.history.pushState({ ...window.history.state, hedonePlayer: true }, '');
    setPlayerData({
      id: series.id,
      season,
      episode,
    });
    setShowPlayer(true);
  };

  const closePlayer = () => {
    if ("mediaSession" in navigator) navigator.mediaSession.metadata = null;
    if (user && watchStartedAt.current && playerData) {
      const elapsed = Math.max(0, Math.round((Date.now() - watchStartedAt.current) / 1000));
      const key = `tv_${id}`;
      const previous = experience.progress?.[key] || {};
      saveExperienceItem("progress", key, {
        ...previous, id:Number(id), mediaType:"tv", title:series.name,
        posterPath:series.poster_path || null, backdropPath:series.backdrop_path || null,
        season:playerData.season, episode:playerData.episode, durationSeconds:2700,
        progressSeconds:Math.min(2592, (previous.progressSeconds || 0) + elapsed),
        lastWatchedAt:Date.now(),
      }).catch(() => {});
      watchStartedAt.current = null;
    }
    const exitFullscreen =
      document.exitFullscreen ||
      document.webkitExitFullscreen ||
      document.msExitFullscreen;
    if (document.fullscreenElement || document.webkitFullscreenElement) {
      Promise.resolve(exitFullscreen?.call(document)).catch(() => {});
    }
    if (window.history.state?.hedonePlayer) window.history.back();
    else setShowPlayer(false);
  };

  const togglePlayerFullscreen = async () => {
    try {
      const fullscreenElement =
        document.fullscreenElement || document.webkitFullscreenElement;
      if (fullscreenElement) {
        const exitFullscreen =
          document.exitFullscreen ||
          document.webkitExitFullscreen ||
          document.msExitFullscreen;
        await Promise.resolve(exitFullscreen?.call(document));
      } else {
        const player = playerOverlayRef.current;
        const requestFullscreen =
          player?.requestFullscreen ||
          player?.webkitRequestFullscreen ||
          player?.msRequestFullscreen;
        await Promise.resolve(requestFullscreen?.call(player));
      }
    } catch (error) {
      console.error("Unable to change fullscreen mode", error);
    }
  };

  useEffect(() => {
    const handlePlayerBack = () => setShowPlayer(false);
    window.addEventListener('popstate', handlePlayerBack);
    return () => window.removeEventListener('popstate', handlePlayerBack);
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => {
      const fullscreenElement =
        document.fullscreenElement || document.webkitFullscreenElement;
      setIsPlayerFullscreen(fullscreenElement === playerOverlayRef.current);
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener(
        "webkitfullscreenchange",
        handleFullscreenChange
      );
    };
  }, []);

  useEffect(() => {
    if (!showPlayer) return undefined;
    const scrollY = window.scrollY;
    document.body.classList.add('player-open');
    document.body.style.top = `-${scrollY}px`;
    return () => {
      document.body.classList.remove('player-open');
      document.body.style.top = '';
      window.scrollTo(0, scrollY);
    };
  }, [showPlayer]);

  useEffect(() => {
    const fetchSeriesDetails = async () => {
      try {
        const seriesEndpoint = `https://api.themoviedb.org/3/tv/${id}?api_key=${apiKey}&append_to_response=credits`;
        const similarSeriesEndpoint = `https://api.themoviedb.org/3/tv/${id}/similar?api_key=${apiKey}`;
        const imagesEndpoint = `https://api.themoviedb.org/3/tv/${id}/images?api_key=${apiKey}`;

        const [seriesResponse, imagesResponse, ...responses] = await Promise.all([
          axios.get(seriesEndpoint),
          axios.get(imagesEndpoint),
          axios.get(`${similarSeriesEndpoint}&page=1`),
          axios.get(`${similarSeriesEndpoint}&page=2`),
          axios.get(`${similarSeriesEndpoint}&page=3`),
        ]);

        const logos = imagesResponse.data.logos || [];
        const enLogo = logos.find(logo => logo.iso_639_1 === 'en');
        setLogoUrl(enLogo ? `https://image.tmdb.org/t/p/original${enLogo.file_path}` : null);

        const combinedSeries = responses.flatMap((response) => response.data.results);

        await preloadImage(
          seriesResponse.data.backdrop_path
            ? `https://image.tmdb.org/t/p/original${seriesResponse.data.backdrop_path}`
            : null
        );
        setSeries(seriesResponse.data);
        setSimilarSeries(combinedSeries.slice(0, 50));

        if (seriesResponse.data.seasons && seriesResponse.data.seasons.length > 0) {
          const episodesEndpoint = `https://api.themoviedb.org/3/tv/${id}/season/${selectedSeason}?api_key=${apiKey}`;
          const episodesResponse = await axios.get(episodesEndpoint);
          setEpisodes(episodesResponse.data.episodes || []);
        }
      } catch (error) {
        console.error(error);
      }
    };

    fetchSeriesDetails();
  }, [apiKey, id, selectedSeason]);

  if (!series) {
    return (
      <div className="page-loader-layer">
        <DetailGlassLoading type='series' />
      </div>
    );
  }

  return (
    <div className="page-stage series-detail-page">
      {showLoader && (
        <div className="page-loader-layer is-exiting">
          <DetailGlassLoading type='series' />
        </div>
      )}
        <div className={`page-reveal ${isContentVisible ? 'is-visible' : ''}`} >
          <div
            style={{
              height: "650px",
              backgroundImage: `url(https://image.tmdb.org/t/p/original${series.backdrop_path})`,
              backgroundSize: "cover",
              position: "relative",
            }}
            className="series-cover"
          >
            <div
              style={{
                position: "absolute",
                bottom: 0,
                width: "100%",
                height: "60%",
                background: "linear-gradient(to top, rgba(0, 0, 0, 0.8), transparent)",
              }}
            ></div>
            <div className="series-hero-content">
              <span className="detail-eyebrow">HEDONE SERIES <i /> {series.status || 'SERIES'}</span>
              <h1 className="series-title-heading">
              {logoUrl ? (
                <img
                  src={logoUrl}
                  onError={() => setLogoUrl(null)}
                  alt={series.name}
                  style={{
                    maxWidth: "23vw",
                    width: "420px",
                    minWidth: "180px",
                    height: "auto",
                    marginBottom: "18px",
                    filter: "drop-shadow(0 4px 24px rgba(0,0,0,0.7))",
                  }}
                  className="series-logo"
                />
              ) : (
                <span className="series-title">{series.name}</span>
              )}
              </h1>
              <div className="series-facts">
                <span className="audience-score">{series.vote_average > 0 ? `${series.vote_average.toFixed(1)} / 10` : 'Not rated'}<small>TMDB</small></span>
                <span>{series.first_air_date?.slice(0, 4)}</span>
                <span>{series.number_of_seasons} {series.number_of_seasons === 1 ? 'season' : 'seasons'}</span>
                <span>{series.number_of_episodes} episodes</span>
                
              </div>
              <DetailSynopsis key={series.id} text={series.overview} className="series-summary" />
              <div className="series-genres">{series.genres?.slice(0, 3).map(genre => <span key={genre.id}>{genre.name}</span>)}</div>
              <a className="episode-jump" href="#series-episodes">Browse episodes</a>
              <Button
                variant="dark"
                onClick={handleWatchNow}
                className="series-hero-btn"
              >
                Play S{selectedSeason} E1
              </Button>
              <div className="detail-library-actions">
                <WatchlistButton item={series} mediaType="tv" />
                <LikeButton item={series} mediaType="tv" />
              </div>
            </div>
            {showPlayer && playerData && createPortal(
              <div
                className={`video-player-overlay ${controlsVisible ? "controls-visible" : "controls-hidden"}`}
                ref={playerOverlayRef}
                onPointerMove={showControls}
              >
                <Button
                  variant="light"
                  className="video-player-close"
                  aria-label="Close player"
                  onClick={closePlayer}
                >
                  <FiX aria-hidden="true" /><span>Close</span>
                </Button>
                <Button
                  variant="light"
                  className={`video-player-expand ${isPlayerFullscreen ? "is-fullscreen" : ""}`}
                  onClick={togglePlayerFullscreen}
                  aria-label={
                    isPlayerFullscreen ? "Exit full screen" : "Enter full screen"
                  }
                  title={isPlayerFullscreen ? "Exit full screen" : "Full screen"}
                >
                  {isPlayerFullscreen ? "×" : "⛶"}
                </Button>
                <iframe
                  src={`https://vidsrc.sh/embed/tv?tmdb=${playerData.id}&season=${playerData.season}&episode=${playerData.episode}&autoplay=1&autonext=1`}
                  width="100%"
                  height="100%"
                  allow="autoplay; encrypted-media; picture-in-picture; web-share"
                  frameBorder="0"
                  allowFullScreen
                  title="Series Player"
                  className="video-player-frame"
                  onMouseEnter={showControls}
                />
                <MovieChat
                  movieId={`tv-${series.id}`}
                  movieTitle={series.name}
                  mode="player"
                  playerControlsVisible={controlsVisible}
                />
                <PlayerExtras item={series} playerRef={playerOverlayRef}/>
              </div>,
              document.body
            )}
          </div>
          <Container className="my-4 text-white">
            <Row className="detail-tabs-row">
              <Tabs defaultActiveKey="details" className="detail-tabs" fill justify>
                <Tab eventKey="review" title="Reviews">
                  <Review id={series.id} />
                </Tab>
                <Tab eventKey="details" title="Series Details">
                  <div className="metadata-panel">
                    <div><span>First aired</span><strong>{series.first_air_date}</strong></div>
                    <div><span>Audience score</span><strong>{series.vote_average?.toFixed(1)} / 10</strong></div>
                    <div><span>Seasons</span><strong>{series.number_of_seasons}</strong></div>
                    <div><span>Episodes</span><strong>{series.number_of_episodes}</strong></div>
                    <div className="wide"><span>Genres</span><strong>{series.genres.map((genre) => genre.name).join(" · ")}</strong></div>
                  </div>
                </Tab>
                <Tab eventKey="cast" title="Cast & Crew">
                  <CastList id={series.id} type="tv" />
                </Tab>
              </Tabs>
            </Row>
            <CollectionPicker key={series.id} item={series} mediaType="tv" />
          <CinematicInsights mediaType="tv" id={series.id} item={series} />
            <Row>
              <section className="episodes-section" id="series-episodes">
              <div className="episodes-header">
                <div><span className="episodes-eyebrow">NOW STREAMING</span><h2>Episodes</h2><p>{episodes.length} episodes in this season</p></div>
                <label className="season-select"><span>Season</span><select value={selectedSeason} onChange={e => setSelectedSeason(Number(e.target.value))}>
                  {series.seasons && series.seasons.map(season => (
                    <option key={season.season_number} value={season.season_number}>
                      {season.season_number} {season.name !== `Season ${season.season_number}` ? `— ${season.name}` : ""}
                    </option>
                  ))}
                </select><FiChevronDown /></label>
              </div>
              <div className="episodes-browser">
                {episodes.length === 0 ? (
                  <p>No episodes found.</p>
                ) : (
                  <ul className="episode-list">
                    {episodes.map((ep) => (
                      <li
                        key={ep.id}
                        tabIndex="0"
                        role="button"
                        onClick={() => handlePlayEpisode(ep.season_number, ep.episode_number)}
                        onKeyDown={event => (event.key === 'Enter' || event.key === ' ') && handlePlayEpisode(ep.season_number, ep.episode_number)}
                      >
                        <div className="episode-visual"><img
                          src={
                            ep.still_path
                              ? `https://image.tmdb.org/t/p/w342${ep.still_path}`
                              : "https://via.placeholder.com/320x180?text=No+Image"
                          }
                          alt={ep.name}
                          loading="lazy"
                        /><span className="episode-play"><FiPlay /></span><small>{String(ep.episode_number).padStart(2, '0')}</small></div>
                        <div className="episode-info">
                          <div className="episode-title">
                            <span>Episode {ep.episode_number}</span>{ep.name}
                          </div>
                          <div className="episode-meta">
                            {ep.air_date && (
                              <>
                                <FiCalendar />
                                {new Date(ep.air_date).toLocaleDateString(undefined, {
                                  month: "short", day: "numeric", year: "numeric",
                                })}{" "}
                              </>
                            )}
                            {ep.runtime ? <><FiClock /> {ep.runtime} min</> : ""}
                          </div>
                          <div className="episode-overview">
                            {ep.overview || (
                              <span style={{ color: "#888" }}>No description available.</span>
                            )}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              </section>
            </Row>
            {/* --- End Episodes Section --- */}

            <h2 className="mt-4" style={{ paddingBottom: '30px' }}>Similar Series</h2>
            <Row className="overflow-auto flex-nowrap gx-3" style={{ scrollbarWidth: "none", paddingBottom: '45px' }}>
              {similarSeries.map((similar) => (
                <Col key={similar.id} className="d-flex">
                  <Card className="position-relative" style={{ width: "300px", height: "400px", overflow: "hidden" }}>
                    <Card.Img
                      src={`https://image.tmdb.org/t/p/w500${similar.poster_path}`}
                      alt={similar.name}
                      style={{ height: "100%", objectFit: "cover" }}
                      onError={e => {
                        e.target.onerror = null;
                        e.target.src = "https://via.placeholder.com/300x400?text=" + encodeURIComponent(similar.name);
                      }}
                    />
                    <div
                      className="position-absolute top-0 start-0 w-100 h-100  bg-opacity-0 d-flex flex-column justify-content-end text-white p-3"
                      style={{
                        transition: "background-color 0.3s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "rgba(0, 0, 0, 0.6)";
                        e.currentTarget.querySelector(".info").style.opacity = "1";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "rgba(0, 0, 0, 0)";
                        e.currentTarget.querySelector(".info").style.opacity = "0";
                      }}
                    >
                      <div
                        className="info"
                        style={{
                          opacity: 0,
                          transition: "opacity 0.3s ease",
                        }}
                      >
                        <h5 className="mb-2">{similar.name}</h5>
                        <p className="mb-2">Average Rating : {similar.vote_average}</p>
                        <p>Release Date : {similar.first_air_date}</p>
                        <Button variant="light" onClick={() => navigate(`/tv/${similar.id}`)}>
                          Watch now 
                        </Button>
                      </div>
                    </div>
                  </Card>
                </Col>
              ))}
            </Row>
          </Container>
          {!showPlayer && (
            <MovieChat movieId={`tv-${series.id}`} movieTitle={series.name} />
          )}
        </div>
    </div>
  );
};

export default SeriesDetails;
